import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { apiClient } from '@/lib/api/api-client';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { formatNumber, formatDateTime } from '@/lib/utils/format';
import { Sparkline } from '@/features/dashboard/components/sparkline';

interface ExchangeRateHistoryEntry {
  id: string;
  currencyCode: string;
  rate: number;
  source: string;
  askAvg: number | null;
  bidAvg: number | null;
  exchangeCount: number;
  createdAt: string;
}

interface ExchangeRateHistoryDialogProps {
  currencyCode: string | null;
  currencyName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExchangeRateHistoryDialog({
  currencyCode,
  currencyName,
  open,
  onOpenChange,
}: ExchangeRateHistoryDialogProps) {
  const [history, setHistory] = useState<ExchangeRateHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState('7');

  useEffect(() => {
    if (!currencyCode || !open) return;
    setLoading(true);
    apiClient
      .get<ExchangeRateHistoryEntry[]>(
        `${ENDPOINTS.CURRENCY_RATE_HISTORY}?code=${currencyCode}&days=${days}`
      )
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [currencyCode, open, days]);

  const chartData = useMemo(() => history.map((h) => h.rate), [history]);

  const stats = useMemo(() => {
    if (history.length === 0) return null;
    const rates = history.map((h) => h.rate);
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    const current = rates[rates.length - 1];
    const first = rates[0];
    const variation = first > 0 ? ((current - first) / first) * 100 : 0;
    return { min, max, current, variation };
  }, [history]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Historial de Tipo de Cambio</DialogTitle>
          <DialogDescription>
            {currencyCode}
            {currencyName ? ` — ${currencyName}` : ''} / BOB — Datos obtenidos automaticamente cada
            30 minutos desde exchanges P2P (Binance, Bybit, Bitget) via CriptoYa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filtro de periodo */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Periodo:</span>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Último día</SelectItem>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                  <SelectItem value="90">90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {stats && (
              <Badge variant={stats.variation >= 0 ? 'success' : 'destructive'} className="text-xs">
                {stats.variation >= 0 ? (
                  <TrendingUp className="w-3 h-3 mr-1 inline" />
                ) : (
                  <TrendingDown className="w-3 h-3 mr-1 inline" />
                )}
                {stats.variation >= 0 ? '+' : ''}
                {stats.variation.toFixed(2)}% en el periodo
              </Badge>
            )}
          </div>

          {/* Grafico sparkline grande */}
          {chartData.length > 1 && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <div className="flex items-center justify-center">
                <Sparkline
                  data={chartData}
                  width={560}
                  height={120}
                  color={stats && stats.variation >= 0 ? '#10b981' : '#ef4444'}
                />
              </div>
            </div>
          )}

          {/* Stats resumen */}
          {stats && (
            <div className="grid grid-cols-4 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Actual</p>
                <p className="text-lg font-bold font-mono">{formatNumber(stats.current, 4)}</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Mínimo</p>
                <p className="text-lg font-semibold font-mono text-green-600">
                  {formatNumber(stats.min, 4)}
                </p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Máximo</p>
                <p className="text-lg font-semibold font-mono text-red-500">
                  {formatNumber(stats.max, 4)}
                </p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Registros</p>
                <p className="text-lg font-semibold">{history.length}</p>
              </div>
            </div>
          )}

          {/* Tabla de historial */}
          {loading ? (
            <p className="text-center text-muted-foreground py-4">Cargando historial...</p>
          ) : history.length === 0 ? (
            <div className="text-center py-6 space-y-2">
              <p className="text-muted-foreground font-medium">Sin registros de historial</p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Los datos se generan automáticamente cada 30 minutos cuando el backend esta
                corriendo. Tambien puedes forzar una sincronizacion con el boton "Sincronizar TC" en
                la página de Divisas.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Rate (Avg)</TableHead>
                    <TableHead className="text-right">Compra (Bid)</TableHead>
                    <TableHead className="text-right">Venta (Ask)</TableHead>
                    <TableHead className="text-center">Exchanges</TableHead>
                    <TableHead className="text-center">Var.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...history].reverse().map((entry, idx, arr) => {
                    const prev = arr[idx + 1];
                    const diff = prev ? entry.rate - prev.rate : 0;
                    return (
                      <TableRow key={entry.id}>
                        <TableCell className="text-sm">{formatDateTime(entry.createdAt)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatNumber(entry.rate, 4)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-600">
                          {entry.bidAvg ? formatNumber(entry.bidAvg, 4) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-red-500">
                          {entry.askAvg ? formatNumber(entry.askAvg, 4) : '-'}
                        </TableCell>
                        <TableCell className="text-center text-sm">{entry.exchangeCount}</TableCell>
                        <TableCell className="text-center">
                          {diff > 0 ? (
                            <TrendingUp className="w-3.5 h-3.5 text-green-600 mx-auto" />
                          ) : diff < 0 ? (
                            <TrendingDown className="w-3.5 h-3.5 text-red-500 mx-auto" />
                          ) : (
                            <Minus className="w-3.5 h-3.5 text-muted-foreground mx-auto" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExchangeRateHistoryDialog;
